import { useCallback, useEffect, useState } from "react";
import { clinicalApi, type DailySummary, type RiskQueueRow } from "../../lib/api/clinical";
import { formatDateTime } from "./format";

function csvCell(value: unknown): string {
  let text = "";
  if (typeof value === "string") text = value;
  else if (typeof value === "number" || typeof value === "boolean") text = String(value);
  return `"${text.replaceAll('"', '""')}"`;
}

export function ReportsPanel() {
  const [summary, setSummary] = useState<DailySummary | null>(null);
  const [queue, setQueue] = useState<RiskQueueRow[]>([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const [summaryData, queueData] = await Promise.all([
      clinicalApi.dailySummary(),
      clinicalApi.riskQueue(),
    ]);
    setSummary(summaryData);
    setQueue(queueData);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load().catch(() => {
      setError("加载日报失败");
    });
  }, [load]);

  function exportCsv() {
    const headers = [
      "脱敏编号",
      "病区",
      "床号",
      "风险级别",
      "风险指数",
      "参考感染率",
      "导管天数",
      "主要因素",
      "处置状态",
      "是否超时",
      "评估时间",
    ];
    const lines = [
      headers.map(csvCell).join(","),
      ...queue.map((row) =>
        [
          row.external_id,
          row.ward,
          row.bed,
          row.tier_label,
          row.risk_index,
          row.calibrated_rate_text,
          row.catheter_days,
          row.factors.join("；"),
          row.workflow_state,
          row.overdue ? "是" : "否",
          formatDateTime(row.assessed_at),
        ]
          .map(csvCell)
          .join(","),
      ),
    ];
    const blob = new Blob([`\uFEFF${lines.join("\r\n")}`], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `CAUTI今日重点清单-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  if (error) return <p role="alert">{error}</p>;
  return (
    <div className="view report-view">
      <section className="report-heading">
        <div>
          <h2>CAUTI 院感日报</h2>
          <p>生成时间：{formatDateTime(summary?.generated_at)}</p>
        </div>
        <div className="drawer-actions no-print">
          <button type="button" onClick={exportCsv}>
            导出脱敏高危清单 CSV
          </button>
          <button
            type="button"
            className="primary"
            onClick={() => {
              window.print();
            }}
          >
            打印 / 保存 PDF
          </button>
        </div>
      </section>
      {summary ? (
        <section className="kpi-grid">
          <div className="kpi-card tier-immediate">
            <span className="kpi-value">{summary.immediate_count}</span>
            <span className="kpi-label">立即处理</span>
          </div>
          <div className="kpi-card tier-today">
            <span className="kpi-value">{summary.today_count}</span>
            <span className="kpi-label">今日评估</span>
          </div>
          <div className="kpi-card">
            <span className="kpi-value">{summary.open_workflows}</span>
            <span className="kpi-label">在办工单</span>
          </div>
          <div className="kpi-card">
            <span className="kpi-value">{summary.active_catheters}</span>
            <span className="kpi-label">在管导管</span>
          </div>
          <div className="kpi-card">
            <span className="kpi-value">{summary.pending_reviews}</span>
            <span className="kpi-label">待院感复核</span>
          </div>
          <div className="kpi-card">
            <span className="kpi-value">{summary.confirmed_cauti}</span>
            <span className="kpi-label">确认 CAUTI</span>
          </div>
        </section>
      ) : null}
      <section>
        <h3>今日管理提示</h3>
        <ul className="plain-list">
          <li>高危预警应在 24 小时内完成留置指征复核和病区反馈。</li>
          <li>中危患者纳入当日导管必要性评估，不应仅查看后搁置。</li>
          <li>本清单用于管理优先级，不等同于 CAUTI 临床诊断。</li>
        </ul>
      </section>
    </div>
  );
}
